/**
 * <results-stats> - animates result stat bars and count-up percentages.
 *
 * Contract:
 *   [data-stat-value]   a row with the target percentage in data-stat-value
 *   [data-stat-fill]    bar fill element receiving --rs-fill-width
 *   [data-stat-number]  text element receiving the animated percentage
 */
class ResultsStats extends HTMLElement {
  #observer = null;
  #frame = null;
  #hasRun = false;

  connectedCallback() {
    this.#stop();
    this.#hasRun = false;

    const stats = this.#prepareStats();

    if (stats.length === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      this.#snap(stats);
      return;
    }

    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    this.#reset(stats);

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.#hasRun) continue;
          if (!isMobile && !this.#isMostlyVisible()) continue;

          this.#hasRun = true;
          observer.disconnect();
          this.#observer = null;
          this.#animate(stats);
        }
      },
      {
        threshold: isMobile ? 0 : [0, 0.25, 0.5, 0.75, 1],
        rootMargin: isMobile ? '0px 0px -25% 0px' : '0px',
      },
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  #prepareStats() {
    return Array.from(this.querySelectorAll('[data-stat-value]'), (row) => ({
      fill: row.querySelector('[data-stat-fill]'),
      number: row.querySelector('[data-stat-number]'),
      target: this.#target(row),
      lastNumber: null,
    }));
  }

  #snap(stats) {
    for (const stat of stats) {
      this.#setFill(stat, stat.target);
      this.#setNumber(stat, stat.target);
    }
  }

  #reset(stats) {
    const fills = [];

    for (const stat of stats) {
      if (stat.fill) {
        stat.fill.style.transition = 'none';
        fills.push(stat.fill);
      }

      this.#setFill(stat, 0);
      this.#setNumber(stat, 0);
    }

    this.getBoundingClientRect();

    for (const fill of fills) {
      fill.style.removeProperty('transition');
    }
  }

  #target(row) {
    const value = Number.parseFloat(row.dataset.statValue);

    if (Number.isNaN(value)) return 0;

    return Math.min(100, Math.max(0, value));
  }

  #isMostlyVisible() {
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    const requiredHeight = Math.min(rect.height, viewportHeight);

    return visibleHeight / requiredHeight >= 0.75;
  }

  #setFill(stat, value) {
    if (stat.fill) {
      stat.fill.style.setProperty('--rs-fill-width', `${value}%`);
      stat.fill.style.setProperty('--rs-fill-scale', value === 0 ? '0' : '1');
    }
  }

  #setNumber(stat, value) {
    if (!stat.number) return;

    const roundedValue = Math.round(value);

    if (stat.lastNumber === roundedValue) return;

    stat.number.textContent = `${roundedValue}%`;
    stat.lastNumber = roundedValue;
  }

  #animate(stats) {
    const duration = Math.max(200, Number.parseInt(this.dataset.duration, 10) || 1600);
    const start = performance.now();

    for (const stat of stats) {
      this.#setFill(stat, stat.target);
    }

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);

      for (const stat of stats) {
        this.#setNumber(stat, progress === 1 ? stat.target : stat.target * progress);
      }

      if (progress < 1) {
        this.#frame = requestAnimationFrame(step);
      } else {
        this.#frame = null;
      }
    };

    this.#frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('results-stats')) {
  customElements.define('results-stats', ResultsStats);
}
